import React from "react";
import { useContext, useEffect, useState } from "react";
import { Button, Col, Form, Row, Card, Alert } from "react-bootstrap";

import PortalService from '../../../services/portal.service';
import StateContext from '../../../context/State';
import AddCustomFieldModal from "./AddCustomFieldModal";
import AddIntakeTemplatesModal from "./AddIntakeTemplatesModal";

import type { CustomField } from "../../../types";

function parsePerClientIdentifierMap(identifier: string | undefined | null): Record<string, unknown> | null {
    if (identifier == null || identifier === '') {
        return null;
    }
    try {
        const parsed: unknown = JSON.parse(identifier);
        if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
            return parsed as Record<string, unknown>;
        }
        return null;
    } catch {
        return null;
    }
}

const CustomFields = ({ displayMode = "full", editMode = false, name = '',  }) => {
    const { state } = useContext(StateContext);
    const portalService = new PortalService(state);
    const [showIntakeTemplatesModal, setShowIntakeTemplatesModal] = useState<boolean>(false);
    const [showIndividualFieldsModal, setShowIndividualFieldsModal] = useState<boolean>(false);
    const [customFields, setCustomFields] = useState<any>([]);

    useEffect(() => {
        // adding delay to account for potential empty array
        const timeout = setTimeout(() => {
            getCustomFieldsData();
        }, 2000)

        return () => clearTimeout(timeout)
    }, []);

    const getCustomFieldsData = async () => {
        const response: CustomField = await portalService.getClientPortalCustomFields(state.portalId);
        setCustomFields(response);
    }

    const filteredFields = editMode ? customFields.filter(field => field.PerClient === false) : customFields.filter(field => field.PerClient === true);

    if (displayMode === "simple") {
        return (
            <>
                    {filteredFields.map((field, index) => {
                        const identifierObject = parsePerClientIdentifierMap(field.Identifier);
                        const raw = name && identifierObject ? identifierObject[name] : undefined;
                        const value = raw != null ? String(raw) : '';
                        return (
                            <Col className="col-4 mb-3" key={field.Name + '-custom-field-' + index}>
                                <React.Fragment>
                                    <Form.Label>{field.Name}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        readOnly={true}
                                        value={value}
                                        placeholder={value || 'Not Submitted'}
                                    />
                                </React.Fragment>
                            </Col>
                        );
                    })}
            </>
        );
    }

    return (
        <Card >
            <Card.Header>
                <Card.Title>Custom Fields</Card.Title>
            </Card.Header>

            <Card.Body>
                <Row >
                    {filteredFields.map((field, index) => {
                        const value = field.Value ?? undefined;
                        return (
                            <Col className="col-4 mb-3" key={field.Name + '-custom-field-' + index}>
                                <React.Fragment>
                                    <Form.Label>{field.Name}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        readOnly={true}
                                        value={value}
                                        placeholder={value || 'Not Submitted'}
                                    />
                                </React.Fragment>
                            </Col>
                        );
                    })}
                </Row>
                {customFields.length === 0 && <Alert>No custom fields added. Please click 'Add Individual Customs Fields' below to get started.</Alert>}
                    <br />
                <Row>
                    <Col>
                        <Button className="me-2" onClick={() => setShowIntakeTemplatesModal(true)}>
                            Add Intake Form from Templates
                        </Button>
                        <Button variant="outline-primary" onClick={() => setShowIndividualFieldsModal(true)}>
                            {customFields.length === 0 ? 'Add' : 'Edit'} Individual Custom Fields
                        </Button>
                        <AddIntakeTemplatesModal
                            show={showIntakeTemplatesModal}
                            onHide={() => {
                                getCustomFieldsData();
                                setShowIntakeTemplatesModal(false);
                            }}
                            onSaved={getCustomFieldsData}
                        />
                        <AddCustomFieldModal
                            getCustomFieldsData={getCustomFieldsData}
                            showModal={showIndividualFieldsModal}
                            onHideModal={() => {
                                getCustomFieldsData();
                                setShowIndividualFieldsModal(false);
                            }}
                        />
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    )
}

export default CustomFields;
